import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Input } from "@learn-me/ui";
import { X, Sparkles, Loader2, BookOpen, CheckCircle } from "lucide-react";
import { adminApi } from "../api/adminApi";
import { api } from "@/lib/api";
import toast from "react-hot-toast";
import type { ApiResponse } from "@learn-me/types";

interface GenerateChaptersModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: string;
  existingChaptersCount: number;
  onChaptersGenerated: () => void;
}

interface Chapter {
  id: string;
  title: string;
  position: number;
}

interface GeneratedSection {
  title: string;
  content: string;
}

interface GeneratedChapter {
  title: string;
  description: string;
  sections: GeneratedSection[];
}

export function GenerateChaptersModal({
  isOpen,
  onClose,
  courseId,
  existingChaptersCount,
  onChaptersGenerated,
}: GenerateChaptersModalProps) {
  const [count, setCount] = useState(3);
  const [topic, setTopic] = useState("");
  const [useReferenceDocs, setUseReferenceDocs] = useState(true);
  const [generatedChapters, setGeneratedChapters] = useState<
    GeneratedChapter[]
  >([]);
  const [step, setStep] = useState<"form" | "preview" | "saving">("form");

  const queryClient = useQueryClient();

  // Check if course has reference docs
  const { data: referenceDocs = [] } = useQuery({
    queryKey: ["course-reference-docs", courseId],
    queryFn: () => adminApi.getCourseReferenceDocs(courseId),
    enabled: isOpen,
    staleTime: 30000,
  });

  const hasReferenceDocs = referenceDocs.length > 0;
  const processedDocsCount = referenceDocs.filter((d) => d.isProcessed).length;

  const generateMutation = useMutation({
    mutationFn: () =>
      adminApi.generateAdditionalChapters(courseId, {
        count,
        topic: topic || undefined,
        useReferenceDocs,
      }),
    onSuccess: (data) => {
      setGeneratedChapters(data.chapters);
      setStep("preview");
      toast.success(`${data.chapters.length} capítulos generados`);
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Error al generar capítulos",
      );
    },
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Create chapters one by one with their sections
      for (let i = 0; i < generatedChapters.length; i++) {
        const chapter = generatedChapters[i];
        if (!chapter) continue;

        // Generate HTML content from sections for Chapter.content (compatibility)
        const htmlContent = chapter.sections
          .map((s) => `<h2>${s.title}</h2>\n${s.content}`)
          .join("\n\n");

        // Create the chapter first
        const response = await api.post<ApiResponse<Chapter>>("/chapters", {
          courseId,
          title: chapter.title,
          description: chapter.description,
          content: htmlContent,
          position: existingChaptersCount + i + 1,
          isFree: false,
          isPublished: false,
        });

        const createdChapter = response.data.data;
        if (!createdChapter) continue;

        // Create sections for this chapter
        for (let j = 0; j < chapter.sections.length; j++) {
          const section = chapter.sections[j];
          if (!section) continue;

          await adminApi.createSection(createdChapter.id, {
            title: section.title,
            content: section.content,
            position: j + 1,
          });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["course-edit", courseId] });
      toast.success("Capítulos guardados exitosamente");
      onChaptersGenerated();
      handleClose();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Error al guardar capítulos",
      );
      setStep("preview");
    },
  });

  const handleClose = () => {
    setCount(3);
    setTopic("");
    setUseReferenceDocs(true);
    setGeneratedChapters([]);
    setStep("form");
    onClose();
  };

  const handleGenerate = () => {
    generateMutation.mutate();
  };

  const handleSave = () => {
    setStep("saving");
    saveMutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-2xl mx-4 bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Sparkles className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">
                Generar Capítulos con IA
              </h2>
              <p className="text-sm text-gray-500">
                {step === "form" &&
                  "Configura la generación de nuevos capítulos"}
                {step === "preview" && "Revisa los capítulos generados"}
                {step === "saving" && "Guardando capítulos..."}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={generateMutation.isPending || saveMutation.isPending}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {step === "form" && (
            <div className="space-y-6">
              {/* Count selector */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Cantidad de capítulos a generar
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setCount(n)}
                      className={`px-4 py-2 rounded-lg border transition-colors ${
                        count === n
                          ? "bg-purple-600 text-white border-purple-600"
                          : "bg-white text-gray-700 border-gray-300 hover:border-purple-300"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>

              {/* Topic input */}
              <div>
                <Input
                  label="Tema específico (opcional)"
                  placeholder="Ej: Estrategias avanzadas de marketing digital"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Si no especificas un tema, la IA identificará los temas más
                  importantes que faltan.
                </p>
              </div>

              {/* Reference docs toggle */}
              {hasReferenceDocs && (
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useReferenceDocs}
                      onChange={(e) => setUseReferenceDocs(e.target.checked)}
                      className="mt-1 w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <div>
                      <span className="text-sm font-medium text-purple-800">
                        Usar documentos de referencia
                      </span>
                      <p className="text-xs text-purple-600">
                        {processedDocsCount} de {referenceDocs.length} documento
                        {referenceDocs.length !== 1 ? "s" : ""} procesado
                        {processedDocsCount !== 1 ? "s" : ""}
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Info */}
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex gap-3">
                  <BookOpen className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">
                      Capítulos existentes: {existingChaptersCount}
                    </p>
                    <p className="mt-1">
                      Los nuevos capítulos se añadirán después de los existentes
                      y complementarán el contenido actual del curso.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === "preview" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Se han generado {generatedChapters.length} capítulos con{" "}
                {generatedChapters.reduce(
                  (acc, c) => acc + c.sections.length,
                  0,
                )}{" "}
                secciones en total. Revísalos antes de guardar.
              </p>

              <div className="space-y-3">
                {generatedChapters.map((chapter, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-200"
                  >
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 text-purple-700 text-sm font-medium flex items-center justify-center">
                        {existingChaptersCount + idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900">
                          {chapter.title}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {chapter.description}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {chapter.sections.map((section, sIdx) => (
                            <span
                              key={sIdx}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-700"
                            >
                              {section.title}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === "saving" && (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 text-purple-600 animate-spin" />
              <p className="mt-4 text-gray-600">Guardando capítulos...</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          {step === "form" && (
            <>
              <Button variant="outline" onClick={handleClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleGenerate}
                isLoading={generateMutation.isPending}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Sparkles className="h-4 w-4 mr-2" />
                Generar Capítulos
              </Button>
            </>
          )}

          {step === "preview" && (
            <>
              <Button variant="outline" onClick={() => setStep("form")}>
                Volver
              </Button>
              <Button variant="outline" onClick={handleClose}>
                Descartar
              </Button>
              <Button
                onClick={handleSave}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Guardar Capítulos
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
